/**
 * Aparência + microcopy do widget Clerk na consola HQ.
 *
 * Sem isto, o `<SignIn>` renderiza o default do Clerk: cartão BRANCO sobre a
 * consola escura, em inglês, com o seu próprio card/sombra por cima do nosso
 * `.auth-card` (cartão dentro de cartão) e link de registo público.
 *
 * Estratégia: o cartão é NOSSO (`.auth-card`); ao Clerk tiramos-lhe toda a
 * chrome (fundo, borda, sombra, largura fixa) e deixamos só os campos. Assim há
 * um só cartão e o widget passa a fluir dentro dele em qualquer largura.
 */
// Sem `import type { Appearance } from '@clerk/types'`: o @clerk/types é uma
// dependência TRANSITIVA do clerk-react, não direta — importá-lo daqui
// quebra o typecheck (TS2307) e criaria um acoplamento a uma versão que não
// controlamos. O ClerkProvider valida a forma no ponto de uso.

// @ds-allow-hex: o `appearance` do Clerk é config de um SDK TERCEIRO — só aceita hex
// literal, não resolve `var(--…)`. ESPELHA a paleta do MDS e é IDÊNTICA à da app lean
// (apps/lean/src/app/providers/AuthProvider.tsx): a entrada na consola é o mesmo
// ecrã que a entrada no www, claro por omissão e a seguir o tema escolhido.
const PALETTES = {
  light: { text: '#17132B', muted: '#5A5476', input: '#FAF9FD', border: '#E2DEF2', hover: '#F1EEFB', primary: '#5D3FD3', deep: '#4B31B0', danger: '#D6336C' },
  dark: { text: '#f4f2fb', muted: '#9a92b6', input: '#080611', border: '#241d3d', hover: '#1c1633', primary: '#7B61FF', deep: '#5D3FD3', danger: '#ff3b5c' },
};

export function buildClerkAppearance(mode: 'light' | 'dark') {
  const c = PALETTES[mode];
  return {
    variables: {
      colorPrimary: c.primary,
      colorBackground: 'transparent',
      colorText: c.text,
      colorTextSecondary: c.muted,
      colorInputBackground: c.input,
      colorInputText: c.text,
      colorDanger: c.danger,
      colorNeutral: c.text,
      borderRadius: '12px',
      fontFamily: "'Satoshi','Inter',-apple-system,sans-serif",
    },
    elements: {
      // O cartão é o nosso: tirar a chrome toda do Clerk e deixá-lo fluir.
      rootBox: { width: '100%' },
      cardBox: { width: '100%', boxShadow: 'none', border: 'none' },
      card: { width: '100%', background: 'transparent', boxShadow: 'none', border: 'none', padding: '8px 0 0' },
      // O nosso .auth-card já diz "Marcianus HQ" + subtítulo.
      header: { display: 'none' },
      // Consola INTERNA: ninguém se auto-regista.
      footerAction: { display: 'none' },
      // White-label total: fora o rodapé "Secured by Clerk" e o logótipo Clerk.
      footer: { display: 'none' },
      logoBox: { display: 'none' },
      logoImage: { display: 'none' },
      socialButtonsBlockButton: { background: c.input, border: `1px solid ${c.border}`, color: c.text, minHeight: 44, '&:hover': { background: c.hover } },
      formFieldInput: { background: c.input, border: `1px solid ${c.border}`, color: c.text, minHeight: 44, fontSize: 16 },
      formFieldLabel: { color: c.muted },
      formButtonPrimary: { background: `linear-gradient(135deg, ${c.primary}, ${c.deep})`, color: '#fff', border: 'none', fontWeight: 700, textTransform: 'none', fontSize: '14px', minHeight: 44 },
      dividerLine: { background: c.border },
      dividerText: { color: c.muted },
    },
  };
}

/**
 * Microcopy pt-PT. Inline em vez de `@clerk/localizations`: são meia dúzia de
 * strings visíveis e assim controlamos o tom (o pacote traduz genericamente,
 * e "Welcome back!" ficaria "Bem-vindo de volta!" — não é como falamos aqui).
 */
const clerkLocalizationPt = {
  socialButtonsBlockButton: 'Continuar com {{provider|titleize}}',
  dividerText: 'ou',
  formFieldLabel__emailAddress: 'Email',
  formFieldLabel__password: 'Palavra-passe',
  formFieldInputPlaceholder__emailAddress: 'o-teu-email@marcianusexperience.com',
  formFieldInputPlaceholder__password: 'A tua palavra-passe',
  formButtonPrimary: 'Entrar',
  signIn: {
    start: { title: 'Entrar', subtitle: '', actionText: '', actionLink: '' },
    password: { title: 'Palavra-passe', subtitle: '', actionLink: 'Usar outro método' },
    forgotPasswordAlternativeMethods: { blockButton__resetPassword: 'Repor a palavra-passe' },
  },
} as const;

import { enUS, frFR, esES } from '@clerk/localizations';

/** Localização do Clerk por idioma: pt = microcopy da casa; os restantes usam o pacote oficial. */
export function clerkLocalizationFor(locale: 'pt' | 'en' | 'fr' | 'es') {
  return { pt: clerkLocalizationPt, en: enUS, fr: frFR, es: esES }[locale];
}
export const clerkLocalization = clerkLocalizationPt;
